import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalize } from '../lib/merchantMatcher.js';

const newId = () => `mr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const useMerchantStore = create(
  persist(
    (set, get) => ({
      overrides: {},      // keyed by normalize(merchantName)
      splitRules: {},     // keyed by normalize(merchantName)
      pendingReviews: [], // queue items

      // ── Override actions (Task 10) ─────────────────────
      confirmReview: (reviewId, edits) => {
        const review = get().pendingReviews.find(r => r.id === reviewId);
        if (!review) {
          console.warn(`[merchantStore] confirmReview: unknown id ${reviewId}`);
          return;
        }
        const merchant = edits?.merchantName || review.suggestedMerchant;
        if (merchant) {
          const key = normalize(merchant);
          const existing = get().overrides[key];
          set(state => ({
            overrides: {
              ...state.overrides,
              [key]: {
                category: edits?.category ?? review.suggestedCategory,
                deductible: edits?.deductible ?? review.suggestedDeductible,
                autoConfirmed: existing?.autoConfirmed ?? false,
                confirmationCount: (existing?.confirmationCount ?? 0) + 1,
                ignored: false, // confirming clears ignored
                lastUsedAt: new Date().toISOString(),
              },
            },
          }));
        }
        set(state => ({
          pendingReviews: state.pendingReviews.filter(r => r.id !== reviewId),
        }));
      },

      dismissReview: (reviewId) => {
        const review = get().pendingReviews.find(r => r.id === reviewId);
        if (!review) return;
        // For known merchants (seed/override hit) key by normalized merchant
        // name. For unmatched reviews (suggestedMerchant === null) fall back
        // to the normalized rawDescription so a future add of the same memo
        // matches the ignored override and skips the queue.
        const key = review.suggestedMerchant
          ? normalize(review.suggestedMerchant)
          : normalize(review.rawDescription || '');
        if (key) {
          const existing = get().overrides[key];
          set(state => ({
            overrides: {
              ...state.overrides,
              [key]: {
                ...(existing || { category: null, deductible: null,
                                  autoConfirmed: false, confirmationCount: 0 }),
                ignored: true,
                lastUsedAt: new Date().toISOString(),
              },
            },
          }));
        }
        set(state => ({
          pendingReviews: state.pendingReviews.filter(r => r.id !== reviewId),
        }));
      },

      promoteToAutoConfirm: (merchantName) => {
        if (!merchantName) return;
        const key = normalize(merchantName);
        const existing = get().overrides[key];
        set(state => ({
          overrides: {
            ...state.overrides,
            [key]: {
              category: existing?.category ?? null,
              deductible: existing?.deductible ?? null,
              autoConfirmed: true,
              confirmationCount: existing?.confirmationCount ?? 0,
              ignored: false,
              lastUsedAt: new Date().toISOString(),
            },
          },
        }));
      },

      /**
       * Decrement an override's confirmationCount (used by Undo path in BizReviewTab).
       * No-op if the override doesn't exist. Floors at 0.
       */
      decrementConfirmationCount: (merchantName) => {
        if (!merchantName) return;
        const key = normalize(merchantName);
        set(state => {
          const existing = state.overrides[key];
          if (!existing) return state;
          return {
            overrides: {
              ...state.overrides,
              [key]: { ...existing, confirmationCount: Math.max(0, (existing.confirmationCount ?? 0) - 1) },
            },
          };
        });
      },

      // ── Split rules (Task 11) ──────────────────────────
      saveSplitRule: (merchantName, splits) => {
        if (!merchantName || !Array.isArray(splits) || splits.length < 2) return;
        const key = normalize(merchantName);
        const allPercent = splits.every(s => typeof s.percent === 'number');
        const allAmount = splits.every(s => typeof s.amount === 'number');
        if (!allPercent && !allAmount) return; // mixed/invalid input — don't save
        const mode = allPercent ? 'percent' : 'fixed';

        // Compare to existing rule shape — increment timesUsed only if identical
        const existing = get().splitRules[key];
        const sameShape = existing
          && existing.mode === mode
          && existing.splits.length === splits.length
          && existing.splits.every((s, i) => {
              const n = splits[i];
              return s.category === n.category
                  && s.deductible === n.deductible
                  && (mode === 'percent' ? s.percent === n.percent : s.amount === n.amount);
            });

        set(state => ({
          splitRules: {
            ...state.splitRules,
            [key]: {
              mode,
              splits: splits.map(s => ({ ...s })),
              timesUsed: sameShape ? ((existing.timesUsed ?? 0) + 1) : 1,
              lastUsedAt: new Date().toISOString(),
            },
          },
        }));
      },

      // ── Pending reviews (Task 9) ───────────────────────
      enqueueReview: (expense, match) => {
        if (!match || match.confidence === 'high') return;
        const existing = get().pendingReviews.find(r => r.transactionId === expense.id);
        if (existing) return; // idempotent
        const review = {
          id: newId(),
          transactionId: expense.id,
          rawDescription: expense.note || '',
          amount: expense.amount,
          date: expense.date,
          suggestedMerchant: match.merchantName || null,
          suggestedCategory: match.category || null,
          suggestedDeductible: match.deductible === true,
          suggestedSplit: match.splitRule || null,
          confidence: match.confidence,
          status: 'pending',
        };
        set(state => ({ pendingReviews: [...state.pendingReviews, review] }));
      },
    }),
    {
      name: 'merchant-memory-v1',
      version: 1,
    }
  )
);
